import { IconNames } from '@dixa/lib-shared-habla';
import type { Channel } from 'lib-shared-types-channels';
import { ValueOf } from 'ts-essentials';

import { PickPropertiesByType } from './_temporary';

export enum HTTPRequestMethod {
  Delete = 'DELETE',
  Get = 'GET',
  Patch = 'PATCH',
  Post = 'POST',
  Put = 'PUT',
}

export enum SaveStatus {
  Failed = -1,
  Unsaved = 0,
  Saving = 1,
  Saved = 2,
}

export enum ContentType {
  Markdown = 'markdown',
  PlainText = 'plaintext',
  HTML = 'html',
  Audio = 'audio',
  Empty = 'none',
}

/** Represents the interface for various conversation channel attributes. */
export interface ConversationChannelAttributes {
  prettyName: string;
  icon: IconNames;
}

/** Array containing the channels that are checked for available agents threshold */
export const conversationChannelsToCheckForThreshold: Channel[] = [
  'email',
  'pstn_phone',
  'contact_form',
  'widget_chat',
];

/** Map of conversation channels and various attributes. */
export const conversationChannelAttributes: ReadonlyMap<
  Channel,
  ConversationChannelAttributes
> = new Map([
  [
    'adachat',
    {
      prettyName: 'Ada Chat',
      icon: 'messenger',
    },
  ],
  [
    'digitalgeniuschat',
    {
      prettyName: 'DigitalGenius Chat',
      icon: 'messenger',
    },
  ],
  [
    'kindlychat',
    {
      prettyName: 'Kindly Chat',
      icon: 'messenger',
    },
  ],
  [
    'callback',
    {
      prettyName: 'Callback',
      icon: 'callback',
    },
  ],
  [
    'contact_form',
    {
      prettyName: 'Contact Form',
      icon: 'mail',
    },
  ],
  [
    'email',
    {
      prettyName: 'Email',
      icon: 'mail',
    },
  ],
  [
    'facebook_messenger',
    {
      prettyName: 'Facebook Messenger',
      icon: 'facebook-messenger',
    },
  ],
  [
    'messenger',
    {
      prettyName: 'Messenger',
      icon: 'messenger',
    },
  ],
  [
    'pstn_phone',
    {
      prettyName: 'Phone',
      icon: 'phone',
    },
  ],
  [
    'sms',
    {
      prettyName: 'SMS',
      icon: 'phone',
    },
  ],
  [
    'twitter',
    {
      prettyName: 'Twitter',
      icon: 'twitter',
    },
  ],
  [
    'voicemail',
    {
      prettyName: 'Voicemail',
      icon: 'voicemail',
    },
  ],
  [
    'whats_app',
    {
      prettyName: 'WhatsApp',
      icon: 'whatsapp',
    },
  ],
  [
    'widget_chat',
    {
      prettyName: 'Chat',
      icon: 'chat',
    },
  ],
]);

export interface ConversationChannelContentTypeMap {
  adachat: ContentType.Markdown | ContentType.PlainText;
  kindlychat: ContentType.Markdown | ContentType.PlainText;
  digitalgeniuschat: ContentType.Markdown | ContentType.PlainText;
  widgetchat: ContentType.Markdown | ContentType.PlainText;
  messenger: ContentType.Markdown | ContentType.PlainText | ContentType.HTML;
  contactform: ContentType.Markdown | ContentType.PlainText;
  email: ContentType.HTML | ContentType.Markdown | ContentType.PlainText;
  facebookmessenger: ContentType.Markdown | ContentType.PlainText;
  whatsapp: ContentType.Markdown | ContentType.PlainText;
  sms: ContentType.Markdown | ContentType.PlainText;
  twitter: ContentType.Markdown | ContentType.PlainText;
  pstnphone: ContentType.Audio;
  voicemail: ContentType.Audio;
  callback: ContentType.Empty;
}

export type ConversationsChannels = keyof ConversationChannelContentTypeMap;

export type ChannelContentType = ValueOf<ConversationChannelContentTypeMap>;

export type SanitizeableChannels = keyof PickPropertiesByType<
  ConversationChannelContentTypeMap,
  ContentType.HTML | ContentType.Markdown
>;

/**
 * Used to group different type of channels by the kind of contact point they
 * belong. I.E: The chanels telephony, callback and sms belong to the same kind,
 * 'phone_number'.
 */
export type ContactPointKind =
  | 'phone_number'
  | 'email'
  | 'facebook'
  | 'twitter'
  | 'instagram'
  | 'smooch';

export type LinkedConversationTypes =
  | 'email_forward'
  | 'follow_up'
  | 'side'
  | null;
export type MessagetextContentType = 'markdown' | 'html' | 'plaintext' | null;

export type AsyncStatus = 'succeeded' | 'failed' | 'loading';

export type OptionType<TValue = string> = {
  label: string;
  value: TValue;
};

type AuthEvent = 'tokenRefreshed' | 'refreshSetupFailed' | 'onVerifyRefresh';
type NetworkEvent = 'beforeHttpRequest' | 'networkOnlineChange';
type PresenceEvent = 'presenceAgentStatusChanged';
type DashboardEvent = 'dashboardQueuesStatusChanged';

export type EventName =
  | AuthEvent
  | NetworkEvent
  | PresenceEvent
  | DashboardEvent;
